import { Controller } from 'stimulus'
import { definitionOf } from 'classes/async-helpers'

/* global Turbolinks */

/**
 * Manages js for the refinance application edit partial (StimulusJS controller)
 *
 * @class RefinanceApplicationController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'currentPage',
    'delayInput',
    'formType',
    'input',
    'next',
    'nextVisible',
    'previous',
    'previousVisible',
    'productType',
    'saveForLater',
    'slug',
    'step',
    'submit',
    'submitStep'
  ]

  /**
   * @function controllers.RefinanceApplicationController.initialize
   * @memberof controllers
   * @description triggers "load" event type actions.
   *
   * @return {undefined} manipulates the dom.
   *
   */
  initialize() {
    this.nextVisibleChangeCallback = () => {
      this._updateNavButtons()
    }

    this.turbolinksLoadCallback = () => {
      if (this.hasNextVisibleTarget) {
        this.nextVisibleTarget.addEventListener('change', this.nextVisibleChangeCallback)
      }
    }

    this.hiddenNavButtonClass = 'multi-form__navigation-button--hidden'
    this.firstStep            = 'refinance_address'
    this.stepEvent            = new CustomEvent('turbolinks:load')
    this.delayedInputs        = {}

    this._updateNavButtons()
    this._checkSaveForLater()
    this._sendGTMDataLayer()
    this._updateSessionLevelLogging()
    this._sendSessionData()
    this._updateGetParams()

    document.addEventListener('turbolinks:load', this.turbolinksLoadCallback.bind(this))
  }

  /**
   * @function Controllers.RefinanceApplicationController.teardown
   * @memberof Controllers
   * @description Removes event listenever from nextVisibleTarget on teardown.
   */
  teardown() {
    document.removeEventListener('turbolinks:load', this.turbolinksLoadCallback.bind(this))
    this.nextVisibleTarget.removeEventListener('change', this.nextVisibleChangeCallback)
  }

  /**
   * @function Controllers.RefinanceApplicationController.next
   * @memberof Controllers
   * @description Moves the multi-step form to the next step, gathering the
   * inputs for the step from the data targets 'input' and sends it off to the backend
   * along with marketing values.
   *
   * @param {Event} event - User initiated click event representing the desire
   * to move forward in the form.
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  next(event) {
    event.preventDefault()
    if ( this.hasNextTarget ) { this.nextTarget.disabled = true }

    if ( !this._stepValid() ) {
      this.nextOn()
      return
    }

    this._storeDelayedInputs()

    const inputs = this.formInputs
    this._addDelayedInputsTo(inputs)

    const data = JSON.stringify(inputs)
    this.saveStepData(data)
  }

  /**
   * @function Controllers.RefinanceApplicationController.prev
   * @memberof Controllers
   * @description Moves the multi-step form to the previous step and updates the
   * UI.
   *
   * @param {Event} event - User initiated click event representing the desire
   * to move forward in the form.
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  prev(event) {
    event.preventDefault()
    const slug = this.data.get('slug')
    const csrfToken = this.data.get('csrf-token')
    fetch(`/apply/refinance/steps/${slug}`, {
      method:      'GET',
      credentials: 'same-origin',
      headers:     {
        'content-type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      mode: 'cors'
    })
      .then(response => response.text())
      .then(html => {
        this.stepTarget.innerHTML = html
        this._updateNavButtons()
        this._updateSessionLevelLogging()
        this._sendGTMDataLayer()
        this._sendSessionData()
        this._updateGetParams()
      })
      .then(() => {
        document.dispatchEvent(this.stepEvent)
      })
  }

  /**
   * @function Controllers.RefinanceApplicationController.nextOn
   * @memberof Controllers
   * @description Enables the next button.
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  nextOn() {
    this.nextTarget.disabled = false
  }

  /**
   * @function Controllers.RefinanceApplicationController.nextOff
   * @memberof Controllers
   * @description Disables the next button.
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  nextOff() {
    this.nextTarget.disabled = true
  }

  /**
   * @function Controllers.RefinanceApplicationController.saveStepData
   * @memberof Controllers
   * @description Sends step form data to update controller AND loads result
   * (sometimes redirects to CAS)
   * @param {String} data - JSON form data
   *
   */
  saveStepData(data) {
    const slug = this.data.get('slug')
    const csrfToken = this.data.get('csrf-token')

    fetch(`/apply/refinance/${slug}`, {
      method:      'PATCH',
      body:        data,
      credentials: 'include',
      headers:     {
        'content-type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      mode: 'cors'
    })
      .then(response => response.text())
      .then(text => {
        let maybeJson
        if (this._isJSON(text)) {
          maybeJson = JSON.parse(text)
        }

        if (maybeJson && maybeJson.action === 'redirect') {
          Turbolinks.visit(maybeJson.redirectUrl)
        } else {
          this._loadStep(text)
        }

        if (this.errors.length > 0) {
          setTimeout(() => { this.errors[0].scrollIntoView({ behavior: 'smooth' }) }, 150)
        } else {
          document.getElementsByClassName('multi-form__page')[0].querySelectorAll('input:not([type=hidden]),select,textarea')[0].focus()
        }
      })
  }

  /**
   * @function Controllers.RefinanceApplicationController.errors
   * @memberof Controllers
   * @description Getter for labels of inputs with error modifier
   *
   * @return {NodeList}
   */
  get errors() {
    return document.getElementsByClassName('multi-form__page')[0].querySelectorAll('label[class$="--error"]')
  }


  // Private

  /**
   * @function Controllers.RefinanceApplicationController._isJSON
   * @memberof Controllers
   * @description Checks if passed in value is in JSON format
   * @private
   *
   * @param {String} maybeJson
   *
   * @return {Boolean}
   *
   */
  _isJSON(maybeJson) {
    try {
      JSON.parse(maybeJson)
    } catch (e) {
      return false
    }

    return true
  }

  /**
   * @function Controllers.RefinanceApplicationController._loadStep
   * @memberof Controllers
   * @description Updates step contents with HTML string
   * @private
   *
   * @param {String} html - HTML string to be inserted
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  _loadStep(html) {
    this.stepTarget.innerHTML = html

    this._updateNavButtons()
    this._checkSaveForLater()
    this._updateSessionLevelLogging()
    this._sendGTMDataLayer()
    this._sendSessionData()
    this._updateGetParams()

    document.dispatchEvent(this.stepEvent)
  }

  /**
   * @function Controllers.RefinanceApplicationController.currentStep
   * @memberof Controllers
   * @description Getter for current step
   * @private
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  get currentStep() {
    return this.currentPageTarget.value
  }

  /**
   * @function Controllers.RefinanceApplicationController.formInputs
   * @memberof Controllers
   * @description Getter for refinance-application inputs
   * @private
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  get formInputs() {
    const inputs = {}

    this.inputTargets.forEach(input => {
      if (input.checked || ![ 'checkbox', 'radio' ].includes(input.type)) {
        if (input.name.match(/\w+\[\]/)) {
          const inputName = input.name.slice(0, -2)

          if (inputs[inputName]) {
            inputs[inputName] += `,${input.value}`
          } else {
            inputs[inputName] = input.value
          }
        } else {
          inputs[input.name] = input.value
        }
      }
    })

    return inputs
  }

  /**
   * @function Controllers.RefinanceApplicationController._stepValid
   * @memberof Controllers
   * @description Provides front end validation through the
   * RequiredFieldController
   *
   * @return {Boolean} valid - Boolean representing step validity.
   *
   */
  _stepValid() {
    const validator = this.application.getControllerForElementAndIdentifier(this.element, 'required-field')
    return validator.validate()
  }

  /**
   * @function Controllers.RefinanceApplicationController._storeDelayedInputs
   * @memberof Controllers
   * @description If delayed inputs are present, stores them in memory on this
   * controller with their ripening dates.
   *
   * @return {undefined}
   *
   */
  _storeDelayedInputs() {
    if (this.hasDelayInputTarget) {
      this.delayInputTargets.forEach(input => {
        const key = input.name
        const value = input.value
        const delayUntil = input.dataset.delayInputUntil

        if (this.delayedInputs[delayUntil] === undefined) {
          this.delayedInputs[delayUntil] = {}
        }

        this.delayedInputs[delayUntil][key] = value
      }, this)
    }
  }

  /**
   * @function Controllers.RefinanceApplicationController._addDelayedInputsTo
   * @memberof Controllers
   * @description Adds any delayed inputs to the regular input set if it's their
   * ripened time.
   *
   * @param {Object} data - input data
   *
   * @return {undefined}
   *
   */
  _addDelayedInputsTo(data) {
    const inputs = this.delayedInputs[this.currentStep]
    if (inputs) {
      for (const [ key, value ] of Object.entries(inputs)) {
        data[key] = value
      }
    }
  }

  /**
   * @function Controllers.RefinanceApplicationController._sendGTMDataLayer
   * @memberof Controllers
   * @description Adds data to the GTM DataLayer
   * @private
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  _sendGTMDataLayer() {
    window.dataLayer = window.dataLayer || []
    const page = this.currentPageTarget.value
    const applicationStart = (page === 'refinance_address').toString()
    const applicationComplete = [
      'end_fallout',
      'end_maybe_prequalified',
      'end_prequalified',
      'end_something_wrong',
      'form_fallout',
      'form_received'
    ].includes(page).toString()

    window.dataLayer.push({
      'application_start':    applicationStart,
      'application_complete': applicationComplete,
      'current_page':         page,
      'session_uuid':         sessionStorage.getItem('sessionUUID'),
      'event':                'turboPageLoad',
      'product_type':         'refinance',
      'form_type':            'full'
    })
  }

  /**
   * @function Controllers.RefinanceApplicationController._sendSessionData
   * @memberof Controllers
   * @description Sends session data as soon as it becomes available
   * @private
   *
   * @param {Object} sessionData
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  async _sendSessionData(sessionData) {
    try {
      await definitionOf('session_data', 2000)
      sendSessionData(sessionData) // eslint-disable-line no-undef
    } catch (error) {
      return
    }
  }

  /**
   * @function Controllers.RefinanceApplicationController._setSessionData
   * @memberof Controllers
   * @description Sets session data as soon as it becomes available
   * @private
   *
   * @param {String} key
   * @param {String} value
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  async _setSessionData(key, value) {
    try {
      await definitionOf('setSessionData', 2000)
      setSessionData(key, value) // eslint-disable-line no-undef
    } catch (error) {
      return
    }
  }

  /**
   * @function Controllers.RefinanceApplicationController._updateNavButtons
   * @memberof Controllers
   * @description Updates the nav buttons based on the current step
   * @private
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  _updateNavButtons() {
    const submitText = 'Submit Application'

    let submitStep = 'false'
    if (this.hasSubmitStepTarget) {
      submitStep = this.submitStepTarget.value
    }

    this._updatePreviousButton()
    this._updateNextButton()

    if (submitStep === 'true') {
      this.nextTarget.innerHTML = submitText
    } else {
      this.nextTarget.innerHTML = 'Next'
    }
  }

  /**
   * @function Controllers.RefinanceApplicationController._updatePreviousButton
   * @memberof Controllers
   * @description Updates the previous button.
   * @private
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  _updatePreviousButton() {
    const prevVisible = this.previousVisibleTarget.value
    if (prevVisible === 'true') {
      this.previousTarget.classList.remove(this.hiddenNavButtonClass)
      this.previousTarget.blur()
    } else {
      this.previousTarget.classList.add(this.hiddenNavButtonClass)
    }
  }

  /**
   * @function Controllers.RefinanceApplicationController._updateNextButton
   * @memberof Controllers
   * @description Updates the next button.
   * @private
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  _updateNextButton() {
    const nextVisible = this.nextVisibleTarget.value

    this.nextTarget.disabled = false
    this.nextTarget.classList.remove(this.hiddenNavButtonClass)

    if (nextVisible === 'false') {
      this.nextTarget.classList.add(this.hiddenNavButtonClass)
    }
  }

  /**
   * @function Controllers.RefinanceApplicationController._checkSaveForLater
   * @memberof Controllers
   * @description Dispatches saveable event when application is saveable
   * @private
   */
  _checkSaveForLater() {
    if (this.hasSaveForLaterTarget && this.saveForLaterTarget.value == 'true') {
      document.dispatchEvent(new CustomEvent('application-form:saveable'))
    } else {
      document.dispatchEvent(new CustomEvent('application-form:notSaveable'))
    }
  }

  /**
   * @function Controllers.RefinanceApplicationController._updateSessionLevelLogging
   * @memberof Controllers
   * @description Sets data for session.
   * @private
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  _updateSessionLevelLogging() {
    const page = this.currentPageTarget.value
    const applicationStart = (page === 'refinance_address').toString()
    const applicationComplete = [ 'form_received', 'form_fallout' ].includes(page).toString()
    const formType = this.formTypeTarget.value
    const productType = this.productTypeTarget.value
    const slug = this.slugTarget.value

    this._setSessionData('application_complete', applicationComplete)
    this._setSessionData('application_start', applicationStart)
    this._setSessionData('form_type', formType)
    this._setSessionData('product_type', productType)
    this._setSessionData('slug', slug)
    this._setSessionData('step_name', page)
  }

  /**
   * @function Controllers.RefinanceApplicationController._updateGetParams
   * @memberof Controllers
   * @description Push GET params onto URL.
   * @private
   */
  _updateGetParams() {
    const url = new URL(window.location.href)
    url.searchParams.set('current', this.currentPageTarget.value)
    window.history.replaceState(null, '', url)
  }
}
